import React from 'react'
import { View } from 'react-native'

import DAppsIcon from '@common/assets/svg/DAppsIcon'
import ReceiveIcon from '@common/assets/svg/ReceiveIcon'
import SendIcon from '@common/assets/svg/SendIcon'
import SwapAndBridgeIcon from '@common/assets/svg/SwapAndBridgeIcon'
import ShieldTickIcon from '@common/assets/svg/ShieldTickIcon'
import ActivityIcon from '@common/assets/svg/ActivityIcon'
import { isMobileWebApp } from '@common/config/env'
import { useTranslation } from '@common/config/localization'
import { useReceiveSheet } from '@common/contexts/receiveSheetContext'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import useAppMode from '@web/hooks/useAppMode'
import flexbox from '@common/styles/utils/flexbox'
import GlobeIcon from '@common/assets/svg/GlobeIcon'
import { ModeEnum } from '@web/contexts/appModeContext'
import {
  isPrivateModeBlocked,
  PRIVATE_MODE_TOOLTIP,
  PrivateModeReason
} from '@web/utils/privateModeAvailability'

import RouteItem from './RouteItem'
import { RouteItemType } from './RouteItem/RouteItem'

type Props = {
  hasRailgunWallet: boolean
  privateModeReason: PrivateModeReason
}

const Routes = ({ hasRailgunWallet, privateModeReason }: Props) => {
  const { t } = useTranslation()
  const { openReceive } = useReceiveSheet()
  const { mode, railgunEngineInitializing } = useAppMode()
  const hiddenRoutes =
    mode === ModeEnum.PUBLIC
      ? [WEB_ROUTES.transferPrivate, WEB_ROUTES.unshield, WEB_ROUTES.activity]
      : // Swap & Bridge stays hidden in PRIVATE mode: it swaps PUBLIC on-chain funds, not
        // RAILGUN shielded balances, so showing it here would let users swap public funds while
        // the dashboard shows their shield. Private swap needs an unshield→swap→reshield flow
        // that does not exist yet.
        [WEB_ROUTES.transfer, WEB_ROUTES.shield, WEB_ROUTES.activity, WEB_ROUTES.swapAndBridge]

  const routeItems: RouteItemType[] = [
    {
      testID: 'dashboard-button-send',
      icon: SendIcon,
      label: t('Send'),
      route: WEB_ROUTES.transfer,
      scale: 1.08,
      scaleOnHover: 1.18
    },

    {
      testID: 'dashboard-button-send-private',
      icon: SendIcon,
      label: t('Send'),
      route: WEB_ROUTES.transferPrivate,
      scale: 1.08,
      scaleOnHover: 1.18
    },
    {
      testID: 'dashboard-button-swap-and-bridge',
      icon: SwapAndBridgeIcon,
      // Extension popup shows the shorter "Swap"; mobile-web keeps "Swap & Bridge".
      label: isMobileWebApp ? t('Swap & Bridge') : t('Swap'),
      route: WEB_ROUTES.swapAndBridge,
      scale: 0.95,
      scaleOnHover: 1
    },
    {
      testID: 'dashboard-button-receive',
      icon: ReceiveIcon,
      label: t('Receive'),
      route: WEB_ROUTES.receive,
      // The receive modal (ReceiveSheetContent) reads state via the in-process
      // controller store, which only exists in the mobile-web build. In the
      // extension popup that store isn't mounted, so open the dedicated receive
      // route screen instead of the overlay (which would crash and render empty).
      onPress: isMobileWebApp ? () => openReceive() : undefined,
      scale: 1.08,
      scaleOnHover: 1.18
    },
    {
      testID: 'dashboard-button-unshield',
      icon: GlobeIcon,
      label: t('Unshield'),
      route: WEB_ROUTES.unshield,
      scale: 1.08,
      scaleOnHover: 1.18
    },
    {
      testID: 'dashboard-button-shield',
      icon: ShieldTickIcon,
      label: t('Shield'),
      route: WEB_ROUTES.shield,
      scale: 1.08,
      scaleOnHover: 1.18,
      // Disable when no railgun wallet OR the account can never go private
      // (view-only / hardware / raw private-key — even with a stale wallet row).
      disabled: !hasRailgunWallet || isPrivateModeBlocked(privateModeReason),
      disabledTooltip:
        (!hasRailgunWallet || isPrivateModeBlocked(privateModeReason)) &&
        !railgunEngineInitializing &&
        privateModeReason !== 'available'
          ? {
              id: `dashboard-shield-${privateModeReason}`,
              content: t(PRIVATE_MODE_TOOLTIP[privateModeReason])
            }
          : undefined
    },
    {
      testID: 'dashboard-button-activity',
      icon: ActivityIcon,
      label: t('Activity'),
      route: WEB_ROUTES.activity,
      scale: 1.08,
      scaleOnHover: 1.18
    },
    {
      testID: 'dashboard-button-apps',
      icon: DAppsIcon,
      label: t('Apps'),
      route: WEB_ROUTES.apps,
      scale: 0.95,
      scaleOnHover: 1.02
    }
  ]

  const filteredRouteItems = routeItems.filter(
    (routeItem) => !hiddenRoutes.includes((routeItem.filterKey ?? routeItem.route) as string)
  )

  return (
    <View style={[flexbox.directionRow]}>
      {filteredRouteItems.map((routeItem, index) => (
        <RouteItem
          key={routeItem.label}
          routeItem={routeItem}
          index={index}
          routeItemsLength={routeItems.length}
        />
      ))}
    </View>
  )
}

export default React.memo(Routes)
